import { Tooltip } from '@nextui-org/react';
import { motion } from 'framer-motion';
import React, { forwardRef, memo, useCallback, useEffect, useImperativeHandle, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import FastForwardIcon from '~/assets/FastForwardIcon';
import PlusIcon from '~/assets/PlusIcon';
import SendIcon from '~/assets/SendIcon';
import { TooltipContent } from '~/components/HelpTooltip/HelpTooltip.styles';
import { ConversationSection } from '~/types/api.graphql';
import { CUSTOMER_LIST_FILE_ACCEPT } from '~/utils/customerListUpload';

import AddAudiencePopover from './AddAudiencePopover';
import {
  AddAudienceButton,
  AudienceMobileOverlay,
  AutoModeButton,
  ButtonWrapper,
  InputActionsGroup,
  InputActionsRow,
  InputContainer,
  InputWrapper,
  PrivacyDisclaimer,
  SendButton,
} from './AIInsightsSidebar.styles';
import AnimatedInputBorder from './AnimatedInputBorder';
import AudienceMentionInput from './AudienceMentionInput';
import AudiencePickerContent from './AudiencePickerContent';
import {
  AttachmentChip,
  ComposerAttachmentPayload,
  PlusMenu,
  PlusMenuItem,
  useComposerAttachment,
} from './ComposerAttachment';
import { useAIMentionInput } from './useAIMentionInput';
import { useConversationReferenceMentions } from './useConversationReferenceMentions';

export interface AIInsightsSidebarInputHandle {
  focus: () => void;
  reset: () => void;
  /** Attaches a file dropped on the conversation, as **Upload files** does. */
  attachFile: (file: File) => void;
}

interface AIInsightsSidebarInputProps {
  // Context-derived, passed by parent as stable values
  section: ConversationSection | null;
  isOpen: boolean;
  clickCounterCTA: number;
  // Layout / animation
  isExpanded: boolean;
  isInMobile: boolean;
  openAnimKey: number;
  skipExpandAnimation: boolean;
  inputInternalsVisible: boolean;
  // Disabled flags
  isSending: boolean;
  isStreaming: boolean;
  isSessionPending: boolean;
  isSessionLimitReached: boolean;
  // Optional node rendered inside the input container, above the input box
  // (e.g. the session-limit banner) so it aligns with the input in all modes.
  banner?: React.ReactNode;
  // Auto mode toggle; state is owned by the parent, the callback must be stable (useCallback in parent)
  isAutoMode: boolean;
  isAutoModeReady: boolean;
  onToggleAutoMode: () => void;
  // Whether the user can attach a Customer List CSV (the agent's Customer List capability)
  canAttachFiles: boolean;
  // Submit callback — must be stable (useCallback in parent); the prompt may be empty with an attachment
  onSubmit: (prompt: string, attachment?: ComposerAttachmentPayload) => void;
}

type BorderPhase = 'hidden' | 'drawing' | 'visible';

const AIInsightsSidebarInputInner = (
  {
    section,
    isOpen,
    clickCounterCTA,
    isExpanded,
    isInMobile,
    openAnimKey,
    skipExpandAnimation,
    inputInternalsVisible,
    isSending,
    isStreaming,
    isSessionPending,
    isSessionLimitReached,
    banner,
    isAutoMode,
    isAutoModeReady,
    onToggleAutoMode,
    canAttachFiles,
    onSubmit,
  }: AIInsightsSidebarInputProps,
  ref: React.ForwardedRef<AIInsightsSidebarInputHandle>,
) => {
  const { t } = useTranslation();

  const {
    editorRef,
    editorCallbackRef,
    mentionQuery,
    hasContent,
    handleInput,
    handleKeyDown: handleMentionKeyDown,
    handleCopy,
    handleCut,
    handlePaste,
    handleEditorMouseDown,
    insertMention,
    insertReferenceAtCaret,
    saveCaretPosition,
    closeMention,
    serialize,
    reset,
    startTypingPrefill,
  } = useAIMentionInput({
    section,
    animateTyping: true,
    isOpen,
    clickCounterCTA,
  });

  const { results: mentionResults, loading: isLoadingMentions } = useConversationReferenceMentions({
    query: mentionQuery,
  });

  const [isAudiencePopoverOpen, setIsAudiencePopoverOpen] = useState(false);
  const [isPlusMenuOpen, setIsPlusMenuOpen] = useState(false);
  const plusButtonRef = useRef<HTMLButtonElement>(null);
  const plusMenuRef = useRef<HTMLDivElement>(null);
  const inputWrapperRef = useRef<HTMLDivElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const {
    attachment,
    isNotCsv,
    payload: attachmentPayload,
    isBlockingSend: isAttachmentBlockingSend,
    attach: attachFile,
    retry: retryAttachment,
    remove: removeAttachment,
  } = useComposerAttachment();

  // ---------------------------------------------------------------------------
  // Border animation state (mirrors the open-animation logic from the parent)
  // ---------------------------------------------------------------------------
  const [borderPhase, setBorderPhase] = useState<BorderPhase>('visible');

  // Replicate the parent's open/close border-phase effect here so it is
  // co-located with the animation logic that depends on it (prefill, etc.).
  useEffect(() => {
    if (isOpen) {
      if (skipExpandAnimation) {
        setBorderPhase('visible');
        return undefined;
      }
      setBorderPhase('hidden');
      const id = setTimeout(() => setBorderPhase('drawing'), 200);
      return () => clearTimeout(id);
    }
    // Sidebar closed — reset to visible so next open starts clean
    setBorderPhase('visible');
    setIsAudiencePopoverOpen(false);
    setIsPlusMenuOpen(false);
    return undefined;
  }, [isOpen, skipExpandAnimation]);

  // Close the + menu on a click or tap outside it and its button (the button toggles it itself), or on Escape
  useEffect(() => {
    if (!isPlusMenuOpen) return undefined;
    const handleOutside = (e: MouseEvent | TouchEvent) => {
      const target = e.target as Node | null;
      if (target && !plusMenuRef.current?.contains(target) && !plusButtonRef.current?.contains(target)) {
        setIsPlusMenuOpen(false);
      }
    };
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setIsPlusMenuOpen(false);
    };
    document.addEventListener('mousedown', handleOutside);
    document.addEventListener('touchstart', handleOutside);
    document.addEventListener('keydown', handleEscape);
    return () => {
      document.removeEventListener('mousedown', handleOutside);
      document.removeEventListener('touchstart', handleOutside);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [isPlusMenuOpen]);

  const handleBorderAnimComplete = useCallback(() => {
    setBorderPhase('visible');
    setTimeout(() => startTypingPrefill(), 100);
  }, [startTypingPrefill]);

  // ---------------------------------------------------------------------------
  // CTA prefill effect (re-click while sidebar already open)
  // ---------------------------------------------------------------------------
  const prevIsOpenRef = useRef(isOpen);
  const borderPhaseRef = useRef(borderPhase);
  useEffect(() => {
    borderPhaseRef.current = borderPhase;
  }, [borderPhase]);

  useEffect(() => {
    const wasOpen = prevIsOpenRef.current;
    prevIsOpenRef.current = isOpen;
    if (!wasOpen || !isOpen) return undefined;
    // Border animation still running — handleBorderAnimComplete will call startTypingPrefill
    if (borderPhaseRef.current !== 'visible') return undefined;
    const id = setTimeout(() => startTypingPrefill(), 0);
    return () => clearTimeout(id);
  }, [clickCounterCTA, isOpen, startTypingPrefill]);

  // ---------------------------------------------------------------------------
  // Imperative handle
  // ---------------------------------------------------------------------------
  useImperativeHandle(
    ref,
    () => ({
      focus: () => {
        editorRef.current?.focus();
      },
      reset,
      attachFile: (file: File) => {
        if (canAttachFiles) attachFile(file);
      },
    }),
    [editorRef, reset, canAttachFiles, attachFile],
  );

  // ---------------------------------------------------------------------------
  // Submit
  // ---------------------------------------------------------------------------
  const submit = useCallback(() => {
    // Block submit while agent is responding so Enter can't drop the new input
    if (isSending || isStreaming || isSessionPending || isSessionLimitReached || isAttachmentBlockingSend) return;
    const prompt = serialize();
    if (!prompt && !attachmentPayload) return;
    onSubmit(prompt, attachmentPayload ?? undefined);
    reset();
    if (attachmentPayload) removeAttachment();
  }, [
    isSending,
    isStreaming,
    isSessionPending,
    isSessionLimitReached,
    isAttachmentBlockingSend,
    serialize,
    attachmentPayload,
    onSubmit,
    reset,
    removeAttachment,
  ]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLDivElement>) => {
      handleMentionKeyDown(e, { onSubmit: submit });
    },
    [handleMentionKeyDown, submit],
  );

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------
  return (
    <>
      <motion.div
        key={`input-container-${openAnimKey}`}
        initial={skipExpandAnimation ? false : { opacity: 0 }}
        animate={{ opacity: inputInternalsVisible ? 1 : 0 }}
        transition={{ duration: 0.5 }}
        style={{ flexShrink: 0 }}
      >
        <InputContainer $isSidebarInCollapsedMode={!isExpanded}>
          {banner}
          <InputWrapper ref={inputWrapperRef} $borderHidden={borderPhase !== 'visible'}>
            {borderPhase === 'drawing' && <AnimatedInputBorder onAnimationComplete={handleBorderAnimComplete} />}
            <AttachmentChip
              attachment={attachment}
              isNotCsv={isNotCsv}
              onRetry={retryAttachment}
              onRemove={removeAttachment}
            />
            <AudienceMentionInput
              editorRef={editorCallbackRef}
              onInput={handleInput}
              onKeyDown={handleKeyDown}
              onCopy={handleCopy}
              onCut={handleCut}
              onPaste={handlePaste}
              onMouseDown={(e) => {
                if (isAudiencePopoverOpen) setIsAudiencePopoverOpen(false);
                handleEditorMouseDown(e);
              }}
              placeholder={t('aiInsights.inputPlaceholder')}
              disabled={isSending || isSessionPending || isSessionLimitReached}
              mentionQuery={mentionQuery}
              mentionResults={mentionResults}
              isLoadingMentions={isLoadingMentions}
              onSelectMention={insertMention}
              onCloseMention={closeMention}
            />
            <InputActionsRow>
              <InputActionsGroup>
                {canAttachFiles ? (
                  <AddAudienceButton
                    ref={plusButtonRef}
                    type="button"
                    aria-label={t('aiInsights.attachments.plusMenu')}
                    aria-expanded={isPlusMenuOpen}
                    onMouseDown={(e) => {
                      e.preventDefault();
                      saveCaretPosition();
                    }}
                    onClick={() => setIsPlusMenuOpen((prev) => !prev)}
                  >
                    <PlusIcon />
                  </AddAudienceButton>
                ) : (
                  <AddAudienceButton
                    type="button"
                    onMouseDown={(e) => {
                      e.preventDefault();
                      saveCaretPosition();
                    }}
                    onClick={() => setIsAudiencePopoverOpen((prev) => !prev)}
                  >
                    <PlusIcon />
                    {t('aiInsights.addAudience')}
                  </AddAudienceButton>
                )}
                <Tooltip
                  content={
                    <TooltipContent>
                      {t(isAutoMode ? 'aiInsights.autoModeOnTooltip' : 'aiInsights.autoModeOffTooltip')}
                    </TooltipContent>
                  }
                  placement="top"
                  portalClassName="tooltip-wrapper"
                >
                  {/* Locked while a reply runs so switching off can't seem to stop it; a card answer carries the current value. */}
                  <AutoModeButton
                    type="button"
                    $isOn={isAutoMode}
                    aria-pressed={isAutoMode}
                    disabled={!isAutoModeReady || isSending || isStreaming}
                    onClick={onToggleAutoMode}
                  >
                    <FastForwardIcon />
                    {t('aiInsights.autoMode')}
                  </AutoModeButton>
                </Tooltip>
              </InputActionsGroup>
              {canAttachFiles && isPlusMenuOpen && (
                <PlusMenu ref={plusMenuRef} role="menu">
                  <PlusMenuItem
                    type="button"
                    role="menuitem"
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() => {
                      setIsPlusMenuOpen(false);
                      setIsAudiencePopoverOpen(true);
                    }}
                  >
                    {t('aiInsights.addAudience')}
                  </PlusMenuItem>
                  <PlusMenuItem
                    type="button"
                    role="menuitem"
                    onClick={() => {
                      setIsPlusMenuOpen(false);
                      fileInputRef.current?.click();
                    }}
                  >
                    {t('aiInsights.attachments.uploadFiles')}
                  </PlusMenuItem>
                </PlusMenu>
              )}
              {canAttachFiles && (
                <input
                  ref={fileInputRef}
                  type="file"
                  hidden
                  accept={CUSTOMER_LIST_FILE_ACCEPT}
                  onChange={(event) => {
                    const file = event.target.files?.[0];
                    if (file) attachFile(file);
                    // Lets the same file be picked again after removing it
                    event.target.value = '';
                  }}
                />
              )}
              <ButtonWrapper>
                <SendButton
                  onClick={submit}
                  disabled={
                    (!hasContent && !attachmentPayload) ||
                    isAttachmentBlockingSend ||
                    isSending ||
                    isStreaming ||
                    isSessionPending ||
                    isSessionLimitReached
                  }
                  type="button"
                >
                  <SendIcon />
                </SendButton>
              </ButtonWrapper>
            </InputActionsRow>
          </InputWrapper>
        </InputContainer>
        {!isInMobile && (
          <AddAudiencePopover
            anchorRef={inputWrapperRef}
            isOpen={isAudiencePopoverOpen}
            onClose={() => setIsAudiencePopoverOpen(false)}
            onSelectMention={insertReferenceAtCaret}
          />
        )}
      </motion.div>
      <motion.div
        key={`footer-${openAnimKey}`}
        initial={skipExpandAnimation ? false : { opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.3 }}
        style={{ flexShrink: 0 }}
      >
        <PrivacyDisclaimer>{t('aiInsights.privacyDisclaimer')}</PrivacyDisclaimer>
      </motion.div>
      {isInMobile && isAudiencePopoverOpen && (
        <AudienceMobileOverlay>
          <AudiencePickerContent
            onSelectMention={(r) => {
              insertReferenceAtCaret(r);
              setIsAudiencePopoverOpen(false);
            }}
            onClose={() => setIsAudiencePopoverOpen(false)}
            isMobile
          />
        </AudienceMobileOverlay>
      )}
    </>
  );
};

export default memo(forwardRef<AIInsightsSidebarInputHandle, AIInsightsSidebarInputProps>(AIInsightsSidebarInputInner));
